import React, { useState } from "react"
import { Link, NavLink, useHistory, useLocation } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import "../dashboard/dashboard.css"

const NAV = [
  { to: "/admin", label: "Overview", icon: "fa-solid fa-gauge-high", exact: true },
  { to: "/admin/users", label: "Users", icon: "fa-solid fa-users", exact: false },
  { to: "/admin/listings", label: "Listings", icon: "fa-solid fa-building", exact: false },
  { to: "/admin/bookings", label: "Bookings", icon: "fa-solid fa-calendar-check", exact: false },
  { to: "/admin/quote-requests", label: "Quote requests", icon: "fa-solid fa-file-invoice-dollar", exact: false },
  { to: "/admin/contact-submissions", label: "Submissions", icon: "fa-solid fa-inbox", exact: false },
]

const PATH_TITLES = {
  "/admin": "Admin overview",
  "/admin/users": "Users",
  "/admin/listings": "Listings",
  "/admin/bookings": "Bookings",
  "/admin/quote-requests": "Quote requests",
  "/admin/contact-submissions": "Contact submissions",
}

const PATH_DESCRIPTIONS = {
  "/admin": "Monitor platform activity and jump into the work that needs attention.",
  "/admin/users": "Create accounts, update user details, and review account activity.",
  "/admin/listings": "Add, edit, and maintain the properties shown across the website.",
  "/admin/bookings": "Manage bookings, availability requests, and their operational status.",
  "/admin/quote-requests": "Review design, build, and service quote requests from prospects.",
  "/admin/contact-submissions": "Read contact form and listing inquiry submissions.",
}

function getInitials(user) {
  const name = user?.name || user?.email || "Admin"
  return name
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A"
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth()
  const history = useHistory()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const title = PATH_TITLES[location.pathname] || "Admin"
  const description = PATH_DESCRIPTIONS[location.pathname] || "Manage the Vista Hill platform."

  const closeSidebar = () => setSidebarOpen(false)

  const handleLogout = () => {
    logout()
    history.push("/")
  }

  return (
    <div className='dashboard-layout admin-layout'>
      <div
        className={`dashboard-sidebar__backdrop ${sidebarOpen ? "dashboard-sidebar--open" : ""}`}
        onClick={closeSidebar}
        aria-hidden='true'
      />

      <aside className={`dashboard-sidebar admin-sidebar ${sidebarOpen ? "dashboard-sidebar--open" : ""}`}>
        <div className='dashboard-sidebar__brand'>
          <Link to='/admin' onClick={closeSidebar}>
            <img
              src='/images/vista-logo-light.png'
              alt='Vista Hill'
              className='dashboard-sidebar__logo-img'
            />
            {/* <small>Admin workspace</small> */}
          </Link>
        </div>

        <nav className='dashboard-sidebar__nav' aria-label='Admin navigation'>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              exact={item.exact}
              activeClassName='dashboard-sidebar__nav--active'
              onClick={closeSidebar}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className='dashboard-sidebar__footer'>
          <Link to='/dashboard' onClick={closeSidebar}>
            <i className='fa-solid fa-arrow-left'></i>
            User dashboard
          </Link>
          <Link to='/' onClick={closeSidebar}>
            <i className='fa-solid fa-globe'></i>
            View website
          </Link>
        </div>
      </aside>

      <div className='dashboard-main'>
        <header className='dashboard-header admin-header'>
          <button
            type='button'
            className='dashboard-sidebar__toggle'
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label='Toggle admin menu'
          >
            <i className='fa-solid fa-bars'></i>
          </button>

          <div className='dashboard-header__copy'>
            <span>Admin</span>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>

          <div className='dashboard-header__user'>
            <div className='dashboard-avatar admin-avatar' aria-hidden='true'>
              {user?.profile?.avatar ? <img src={user.profile.avatar} alt='' /> : getInitials(user)}
            </div>
            <div className='dashboard-header__identity'>
              <strong>{user?.name || "Administrator"}</strong>
              <span>{user?.email}</span>
            </div>
            <button type='button' onClick={handleLogout}>
              <i className='fa-solid fa-arrow-right-from-bracket'></i>
              <span>Sign out</span>
            </button>
          </div>
        </header>

        <main className='dashboard-content admin-content'>{children}</main>
      </div>
    </div>
  )
}
